// drawer.js — mobile nav drawer: right-side panel (`.drawer`/`.drawer.open`)
// with staggered item entrance, backdrop, Esc/backdrop/close-button close,
// and focus return to the burger. Same guard + rescan() pattern as app.js:
// re-init guard so a duplicate <script> tag is a no-op, and a rescan() hook
// so the Shopify theme editor's `shopify:section:load` (which replaces the
// header section's DOM wholesale) re-binds to the fresh nodes.
window.__tmgScrollLock = window.__tmgScrollLock || {
  n: 0, saved: '',
  lock() { if (this.n++ === 0) { this.saved = document.documentElement.style.overflow; document.documentElement.style.overflow = 'hidden'; } },
  unlock() { if (this.n > 0 && --this.n === 0) { document.documentElement.style.overflow = this.saved; } }
};
// Keyed form, for callers whose close runs whether or not their panel is
// open (an Escape handler on the whole document): each key holds at most
// one lock, so a stray release never undoes somebody else's. Used by
// col-catalogue.js. Added here rather than in the literal above because
// facets.js may have created the object first, without them.
if (!window.__tmgScrollLock.take) {
  window.__tmgScrollLock.keys = {};
  window.__tmgScrollLock.take = function (key) {
    if (!this.keys[key]) { this.keys[key] = true; this.lock(); }
  };
  window.__tmgScrollLock.release = function (key) {
    if (this.keys[key]) { delete this.keys[key]; this.unlock(); }
  };
}

// Shared focus trap for mobile panels (this drawer + facets.js's filter
// sheet). Lives here because drawer.js loads on every template (facets.js
// only loads on collection), like __tmgScrollLock above. Only one
// trap is ever active at a time in practice (both panels close before the
// other opens), so trap() just overwrites the previous container; release()
// is idempotent (safe to call with no active trap).
window.__tmgFocusTrap = window.__tmgFocusTrap || {
  container: null,
  handler: null,
  selector: 'a[href], button:not([disabled]), input, select, textarea, [tabindex]:not([tabindex="-1"])',
  trap(container) {
    if (!container) return;
    this.release();
    this.container = container;
    var self = this;
    this.handler = function (e) {
      if (e.key !== 'Tab') return;
      var focusable = Array.prototype.filter.call(
        self.container.querySelectorAll(self.selector),
        function (el) { return el.offsetParent !== null; }
      );
      if (!focusable.length) return;
      var first = focusable[0];
      var last = focusable[focusable.length - 1];
      if (e.shiftKey) {
        if (document.activeElement === first) {
          e.preventDefault();
          last.focus();
        }
      } else if (document.activeElement === last) {
        e.preventDefault();
        first.focus();
      }
    };
    document.addEventListener('keydown', this.handler);
  },
  release() {
    if (this.handler) {
      document.removeEventListener('keydown', this.handler);
      this.handler = null;
    }
    this.container = null;
  }
};

if (!window.__tmgDrawer) {
  (function () {
    var burger, drawer, backdrop, closeBtn;
    var isOpen = false;

    // A drawer with data-below="<selector>" opens under that element (the
    // phone menu opens under the header row): its bottom edge becomes the
    // panel's top. data-cover="<selector>" names something inside that
    // element the panel may cover (on a phone the pickup band wraps inside
    // the header row, under its first line): when it is showing, the panel
    // starts at its top instead. The page is scroll-locked while the drawer
    // is open, so one measurement per opening holds.
    //
    // Such a drawer also shares the screen with the header row, whose
    // burger has become the X, so the focus trap covers the whole header
    // section rather than the panel alone; otherwise Tab could never reach
    // the control that closes it.
    function trapRoot() {
      if (drawer.dataset.below) return drawer.closest('.shopify-section') || drawer;
      return drawer;
    }

    function openDrawer() {
      if (!drawer || !backdrop || !burger) return;
      if (drawer.dataset.below) {
        var above = document.querySelector(drawer.dataset.below);
        if (above) {
          var edge = above.getBoundingClientRect().bottom;
          var cover = drawer.dataset.cover ? above.querySelector(drawer.dataset.cover) : null;
          if (cover && cover.offsetParent !== null) edge = Math.min(edge, cover.getBoundingClientRect().top);
          drawer.style.setProperty('--dw-top', Math.max(0, Math.round(edge)) + 'px');
        }
      }
      drawer.classList.add('open');
      backdrop.classList.add('open');
      burger.setAttribute('aria-expanded', 'true');
      drawer.removeAttribute('inert');
      drawer.setAttribute('aria-hidden', 'false');
      window.__tmgScrollLock.lock();
      isOpen = true;
      window.__tmgFocusTrap.trap(trapRoot());
      // Keyboard users land on the close control; a tap leaves focus
      // where it was (on the X), so nothing flashes for touch.
      if (closeBtn && burger.matches(':focus-visible')) closeBtn.focus();
    }

    function closeDrawer() {
      if (!drawer || !backdrop || !burger) return;
      if (!isOpen) return;
      drawer.classList.remove('open');
      backdrop.classList.remove('open');
      burger.setAttribute('aria-expanded', 'false');
      window.__tmgScrollLock.unlock();
      window.__tmgFocusTrap.release();
      isOpen = false;
      if (burger.offsetParent !== null) burger.focus();
      drawer.setAttribute('inert', '');
      drawer.setAttribute('aria-hidden', 'true');
    }

    function toggleDrawer() {
      if (isOpen) {
        closeDrawer();
      } else {
        openDrawer();
      }
    }

    // (Re)bind to the current DOM nodes. Safe to call repeatedly: each call
    // re-queries the elements and attaches fresh listeners to whatever is
    // currently in the document (old nodes — and their listeners — are gone
    // once the theme editor replaces the section markup).
    function bind() {
      burger = document.querySelector('.nav-burger');
      drawer = document.getElementById('mobile-drawer');
      backdrop = document.querySelector('.drawer-backdrop');
      closeBtn = drawer ? drawer.querySelector('.drawer-close') : null;
      isOpen = false;

      if (burger) burger.addEventListener('click', toggleDrawer);
      if (backdrop) backdrop.addEventListener('click', closeDrawer);
      if (closeBtn) closeBtn.addEventListener('click', closeDrawer);
    }

    // Global listeners bound once — they read `isOpen`/current element refs
    // via closure, so they keep working across rescans without re-attaching.
    document.addEventListener('keydown', function (e) {
      if (e.key === 'Escape' && isOpen) closeDrawer();
    });
    addEventListener(
      'resize',
      function () {
        if (isOpen && innerWidth > 960) closeDrawer();
      },
      { passive: true }
    );

    function rescan() {
      bind();
    }

    document.addEventListener('shopify:section:load', function () {
      window.__tmgDrawer.rescan();
    });

    bind();
    // close() lets another header control (the search field in the same
    // row) put the menu away before it opens its own panel.
    window.__tmgDrawer = { rescan: rescan, close: closeDrawer };
  })();
}
